<template>
  <video
    v-if="scrubMode === 'video' || scrubMode === 'webCodecsApi'"
    ref="video"
    class="base-video-scrub is-video"
    muted
    playsinline
    :poster="poster ?? undefined"
    :src="scrubMode === 'video' ? (src ?? undefined) : undefined"
    :preload="!props.canBeInitialized || scrubMode !== 'video' ? 'metadata' : 'auto'"
    :style="{ display: scrubMode === 'webCodecsApi' ? 'none' : undefined }"
    @timeupdate="onTimeUpdate"
    @canplaythrough="handleCanPlayThrough"></video>
  <canvas v-if="scrubMode === 'webCodecsApi' || scrubMode === 'frames'" ref="canvas" class="base-video-scrub is-canvas">
  </canvas>
</template>

<script setup lang="ts">
import { gsap } from 'gsap';
import { nextTick, onMounted, onUnmounted, ref, useTemplateRef, watch } from 'vue';
import { useBreakpoint } from '@seb-platform/shared/composables';
import { hasWebCodecsSupport, WebCodecsScrubber } from '@seb-platform/shared/utils/video-scrolling';

const props = withDefaults(
  defineProps<{
    progress?: number;
    src?: string | null;
    poster?: string | null;
    smooth?: boolean;
    isDisabled?: boolean;
    canBeInitialized: boolean;
    serverFramesCount?: number;
  }>(),
  {
    progress: 0,
    smooth: false,
  }
);

const emit = defineEmits<{ (e: 'load'): void }>();

const { isTouch } = useBreakpoint();
const $video = useTemplateRef('video');
const $canvas = useTemplateRef('canvas');
const webCodecsScrubberInstance = ref<WebCodecsScrubber | null>(null);
const videoProgress = ref(0);
const scrubMode = ref<'webCodecsApi' | 'video' | 'frames' | null>(null);
const isInitialized = ref(false);

function onTimeUpdate() {
  if (!$video.value) {
    return;
  }
  $video.value.pause();
}

function updateProgress() {
  let duration = 0;

  if (props.isDisabled) {
    duration = 0;
  } else if (isTouch.value) {
    duration = 0.25;
  } else {
    duration = 0.5;
  }

  // const progress = { value: videoProgress.value };
  gsap.to(videoProgress, {
    value: props.progress,
    duration,
    ease: props.smooth ? 'sine.out' : 'none',
  });
}

function initVideo() {
  if (isInitialized.value || !props.canBeInitialized || !props.src) {
    return;
  }
  isInitialized.value = true;
  if (scrubMode.value === 'webCodecsApi') {
    webCodecsScrubberInstance.value = new WebCodecsScrubber({
      src: props.src,
      videoEl: $video.value!,
      canvasEl: $canvas.value!,
      onReady: () => emit('load'),
    });
  } else if (scrubMode.value === 'video') {
    $video.value!.load();
    $video.value!.pause();
    emit('load');
  } else if (scrubMode.value === 'frames') {
    if (!props.serverFramesCount) {
      throw new Error('Prop `serverFramesCount` is required for "frames" scrub mode');
    }

    webCodecsScrubberInstance.value = new WebCodecsScrubber({
      src: props.src,
      videoEl: $video.value!,
      canvasEl: $canvas.value!,
      onReady: () => emit('load'),
      serverFramesCount: props.serverFramesCount,
    });
  }
}

async function handleCanPlayThrough() {
  if (scrubMode.value === 'video' && $video.value) {
    await $video.value.play();
    requestAnimationFrame(() => {
      $video.value?.pause();
    });
    emit('load');
  }
}

watch(
  () => props.isDisabled,
  () => {
    if (props.isDisabled) {
      gsap.killTweensOf(videoProgress);
    }
  }
);

watch(
  () => props.progress,
  () => {
    void nextTick(updateProgress);
  }
);

watch(
  () => videoProgress.value,
  () => {
    if (scrubMode.value === 'video' && $video.value) {
      if (isNaN(($video.value as HTMLVideoElement)?.duration)) {
        return;
      }
      let updateTimeTo = $video.value.duration * videoProgress.value;
      if (updateTimeTo >= $video.value.duration) {
        updateTimeTo = $video.value.duration - 0.05; // This is needed since Edge shows the first frame if we set it to the video's duration
      }
      window.requestAnimationFrame(() => {
        if ($video.value && $video.value.currentTime !== updateTimeTo) {
          $video.value.currentTime = updateTimeTo;
        }
      });
    } else if (webCodecsScrubberInstance.value) {
      webCodecsScrubberInstance.value.setProgress(videoProgress.value);
    }
  }
);

watch(
  () => props.src,
  () => {
    if (scrubMode.value === 'webCodecsApi' && webCodecsScrubberInstance.value) {
      webCodecsScrubberInstance.value.destroy();
    }
    isInitialized.value = false;
    initVideo();
  }
);

watch(() => props.canBeInitialized, initVideo);

onUnmounted(() => {
  gsap.killTweensOf(videoProgress);
  webCodecsScrubberInstance.value?.destroy();
  webCodecsScrubberInstance.value = null;
});

onMounted(() => {
  if (import.meta.client) {
    const isImageSequence = props.src?.endsWith('.webp') ?? false;

    if (isImageSequence) {
      scrubMode.value = 'frames';
    } else if (hasWebCodecsSupport()) {
      scrubMode.value = 'webCodecsApi';
    } else {
      scrubMode.value = 'video';
    }

    void nextTick(initVideo);
  }
});
</script>

<style src="./SharedBaseVideoScrub.scss" lang="scss" scoped />
